import Vue from 'vue';
import { validate, ValidationProvider } from 'vee-validate';
/**
  Renders a Field context, which approximates vee-validate v4 Field
  across Vue 2 and Vue 3 compatibility: https://vee-validate.logaretm.com/v4/api/form
  difference from vee-validate v4 API includes:
      - Field v-slot prop "on" does not exist in v4, this will need to be deleted on upgrade
        all "on" handlers are applied using the "field" slot-prop in vee-validate v4

  Example usage from a parent component:

    <Form ... >
      ...
      <Field
        v-slot="{ field, on, errors }"
        name="title"
        label="Title"
        rules="required|min:1|max:200"
      >
        <label>
          <span>Title: </span>
          <bf-text-input v-bind="field" v-on="on" :errors="errors" placeholder="Enter title..." />
        <label>
      </Field>
      ...
    </Form>

 */
const Field = Vue.extend({
    name: 'Field',
    components: { ValidationProvider },
    inheritAttrs: false,
    props: {
        as: { type: String, required: false, default: 'span' },
        name: { type: String, required: true },
        label: { type: String, required: false, default: '' },
        rules: { type: String, required: false, default: '' },
        validateOnBlur: { type: Boolean, required: false, default: true },
        validateOnInput: { type: Boolean, required: false, default: false },
        validateOnChange: { type: Boolean, required: false, default: true },
        validateOnUpdate: { type: Boolean, required: false, default: false },
        validateOnModelUpdate: { type: Boolean, required: false, default: false },
    },
    data() {
        return {
            form: undefined,
            initialValue: undefined,
            currentValue: undefined,
        };
    },
    computed: {
        namespacedRules() {
            // combined with custom shim `defineRule` behaviour,
            // this namespacing ensures vee-validate v4 `defineRule()` rule names
            // do not collide with vee-validate v3 `extend()` rule names
            return this.rules && this.rules.split(/\|/g).map(rule => `vv4-${rule}`).join('|');
        },
    },
    watch: {
        initialValue(newValue) {
            if (this.currentValue === undefined) {
                this.currentValue = newValue;
            }
        },
        currentValue(val) {
            this.update(val, this.validateOnModelUpdate || this.validateOnUpdate);
        },
    },
    mounted() {
        // grab parent Form if it exists
        this.form = this._getComponent('Form');
        if (this.form) {
            // set initial value from parent Form
            this.initialValue = this.form._getInitialValue(this.name);
            // expose this field state to the parent Form
            this.form._setFieldReference(this.name, {
                _setInitialValue: (value) => this._setInitialValue(value),
                _setCurrentValue: (value) => this._setCurrentValue(value),
            });
        }
    },
    methods: {
        // find parent Form component
        _getComponent(componentName) {
            let component = null;
            let parent = this.$parent;
            while (parent && !component) {
                if (parent.$options.name === componentName) {
                    component = parent;
                }
                parent = parent.$parent;
            }
            return component || undefined;
        },
        _setInitialValue(value) {
            this.initialValue = value;
        },
        _setCurrentValue(value) {
            this.currentValue = value;
        },
        update(e, shouldValidate) {
            // use value or input target value
            const eventTarget = e?.target;
            const value = eventTarget?.value ?? e;
            // update value
            this.currentValue = value;
            // optionally do a validation (only if applicable and field validation is defined)
            const fieldRef = this.$refs['field'];
            if (fieldRef && shouldValidate) {
                const formValidationSchema = this.form?._getFormValidationSchema();
                if (formValidationSchema) {
                    //
                    // WARNING: these validations types may not work as you expect!
                    //          the formValidationSchema is currently being set to {}
                    //          in conditional FormModal logic and that condition is tested.
                    //
                    //          BUT: using the objects as intended in vee-validate v4
                    //          eg: { email: 'required|min:3' } or { email: utils.isValidEmail }
                    //          may validate *only the field* and *not the form*
                    //          ie. field.errors should be fine, form.errors are not fine
                    //
                    const formValidator = formValidationSchema[fieldRef.name];
                    // validate through custom function supplied to Form validation-schema
                    if (typeof formValidator === 'function') {
                        const result = formValidator(value);
                        if (result !== true) {
                            fieldRef.setErrors([result || 'error']);
                        }
                    }
                    // validate through rules string supplied to Form validation-schema
                    else if (typeof formValidator === 'string') {
                        (async () => {
                            const result = await validate(value, formValidator);
                            fieldRef?.setErrors(result.errors);
                        });
                    }
                    // if formValidator is not defined, the field will not be validated
                }
                // do normal field validation through rules string supplied to Field
                else {
                    fieldRef.validate(value);
                }
            }
            this.form?._setCurrentValue(this.name, value);
        },
        onBlur(e) {
            this.update(e, this.validateOnBlur);
        },
        onInput(e) {
            this.update(e, this.validateOnInput);
        },
        onChange(e) {
            this.update(e, this.validateOnChange);
        },
        onUpdate(e) {
            this.update(e, this.validateOnModelUpdate);
        },
    }
});
export default Field;
